import { useState, useEffect, useRef, useCallback } from 'react';
/**
 * Hook to detect which nav items overflow their container.
 * Uses a measurement container to calculate item widths without affecting layout.
 *
 * Items are hidden from the END of the list first (lowest priority),
 * so the first items in the list are highest priority and stay visible longest.
 *
 * Usage:
 * 1. Apply containerRef to the visible nav container
 * 2. Apply measureRef to a hidden measurement container with all items
 * 3. overflowStartIndex indicates the first item index that should be in the overflow menu
 * 4. Items with index >= overflowStartIndex should be hidden from main nav and shown in overflow menu
 */
export function useOverflowMenu(itemCount) {
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const [overflowStartIndex, setOverflowStartIndex] = useState(-1);
    const checkOverflow = useCallback(() => {
        const container = containerRef.current;
        const measureContainer = measureRef.current;
        if (!container || !measureContainer)
            return;
        // Get the available width for nav items
        const containerStyle = window.getComputedStyle(container);
        const containerPaddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
        const containerPaddingRight = parseFloat(containerStyle.paddingRight) || 0;
        const availableWidth = container.clientWidth - containerPaddingLeft - containerPaddingRight;
        // Get the gap between items
        const gap = parseFloat(containerStyle.gap) || 0;
        // Measure all items in the hidden measurement container
        const items = measureContainer.querySelectorAll('[data-nav-index]');
        const itemWidths = [];
        items.forEach((item) => {
            const el = item;
            const style = window.getComputedStyle(el);
            const marginLeft = parseFloat(style.marginLeft) || 0;
            const marginRight = parseFloat(style.marginRight) || 0;
            // Use getBoundingClientRect for accurate sub-pixel measurements
            const rect = el.getBoundingClientRect();
            itemWidths.push(rect.width + marginLeft + marginRight);
        });
        // Reserve space for the hamburger button (measure it if present, or use fallback)
        const hamburgerBtn = container.parentElement?.querySelector('.cfn-nav-overflow-btn');
        const hamburgerWidth = hamburgerBtn ? hamburgerBtn.getBoundingClientRect().width + 8 : 48; // 8px for gap
        // Calculate how many items fit
        let cumulativeWidth = 0;
        let newOverflowStartIndex = -1;
        for (let i = 0; i < itemWidths.length; i++) {
            // Add gap for items after the first
            const gapForItem = i > 0 ? gap : 0;
            const widthWithGap = itemWidths[i] + gapForItem;
            // Check if this item would fit
            // We need to reserve hamburger space if ANY items will overflow
            const spaceNeeded = cumulativeWidth + widthWithGap;
            const remainingItems = itemWidths.slice(i + 1);
            const totalRemainingWidth = remainingItems.reduce((sum, w, idx) => sum + w + (idx > 0 || i > 0 ? gap : 0), 0);
            // If this item plus remaining items exceed available space, we need to start overflow
            if (spaceNeeded + totalRemainingWidth > availableWidth) {
                // This item or a later item won't fit - need to determine where to start overflow
                // We need hamburger button space, so recalculate with that reserved
                const availableWithHamburger = availableWidth - hamburgerWidth;
                // Find the first item that doesn't fit with hamburger space reserved
                let testWidth = 0;
                for (let j = 0; j < itemWidths.length; j++) {
                    const testGap = j > 0 ? gap : 0;
                    testWidth += itemWidths[j] + testGap;
                    if (testWidth > availableWithHamburger) {
                        newOverflowStartIndex = j;
                        break;
                    }
                }
                break;
            }
            cumulativeWidth = spaceNeeded;
        }
        setOverflowStartIndex(newOverflowStartIndex);
    }, [itemCount]);
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        // Initial check after a brief delay to ensure DOM is ready
        const timeoutId = setTimeout(checkOverflow, 50);
        // Check on resize using ResizeObserver
        const resizeObserver = new ResizeObserver(() => {
            // Use requestAnimationFrame to batch updates
            requestAnimationFrame(checkOverflow);
        });
        resizeObserver.observe(container);
        // Also observe window resize for good measure
        window.addEventListener('resize', checkOverflow);
        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
            window.removeEventListener('resize', checkOverflow);
        };
    }, [checkOverflow]);
    return {
        containerRef,
        measureRef,
        overflowStartIndex,
        hasOverflow: overflowStartIndex >= 0,
    };
}
